import { getBlockContent, type StreamingBlock } from "@storytell/streaming";
import { TbExternalLink } from "solid-icons/tb";
import { createMemo, For, Show } from "solid-js";
import { safeParseBlockJson } from "../../message/parseBlockContent";
import { useChatRuntime } from "../../runtime/ChatRuntime";
import { type NotionDatabaseRowsPayload, parseNotionDatabaseRowsPayload } from "./notionDatabaseRowsSchema";
import { NotionIcon } from "./notionIcon";

interface NotionDatabaseRowsRendererProps {
  block: StreamingBlock;
}

const safeNotionUrl = (url: string | undefined): string | undefined => (url?.startsWith("https://") ? url : undefined);

export const NotionDatabaseRowsRenderer = (props: NotionDatabaseRowsRendererProps) => {
  const rt = useChatRuntime();

  const rawContent = createMemo(
    () =>
      (props.block.finalContent?.type === "text" ? props.block.finalContent.text : undefined) ??
      getBlockContent(props.block.blockId)
  );

  const payload = createMemo<NotionDatabaseRowsPayload | null>(() => {
    const raw = rawContent();
    if (!raw) return null;
    const json = safeParseBlockJson(raw);
    if (!json) return null;
    return parseNotionDatabaseRowsPayload(json);
  });

  const parseFailed = () => !!rawContent() && payload() === null;

  return (
    <Show
      when={!parseFailed()}
      fallback={
        <div class="rounded-lg border border-primary-border bg-panel px-3 py-2 text-xs text-muted-text">
          {rt.t("notion_database_card.parse_error")}
        </div>
      }
    >
      <Show when={payload()}>
        {(data) => (
          // An agent fans these out several per turn, so a miss draws nothing.
          <Show when={data().rows.length > 0 || data().truncated || (data().dataSources?.length ?? 0) > 0}>
            <div class="overflow-hidden rounded-lg border border-primary-border bg-panel">
              <div class="flex items-center gap-2 border-b border-primary-border px-3 py-2">
                <NotionIcon class="size-4 shrink-0" />
                <span class="truncate text-sm font-medium text-primary-text">
                  {data().title || rt.t("notion_database_card.title")}
                </span>
                <span class="ml-auto shrink-0 text-xs text-muted-text">
                  <Show
                    when={(data().totalCount ?? 0) > data().rows.length}
                    fallback={
                      data().rows.length === 1
                        ? rt.t("notion_database_card.results_one")
                        : rt.t("notion_database_card.results", { count: String(data().rows.length) })
                    }
                  >
                    {rt.t("notion_database_card.results_of", {
                      count: String(data().rows.length),
                      total: String(data().totalCount ?? 0),
                    })}
                  </Show>
                </span>
              </div>

              <ul class="divide-y divide-primary-border">
                <For each={data().rows}>
                  {(row) => (
                    <li class="px-3 py-2">
                      <p class="line-clamp-3 text-sm whitespace-pre-wrap text-primary-text">{row.content}</p>
                      <Show when={safeNotionUrl(row.url)}>
                        {(href) => (
                          <a
                            href={href()}
                            target="_blank"
                            rel="noopener noreferrer"
                            class="mt-1 inline-flex items-center gap-1 text-xs text-brand hover:underline"
                          >
                            {rt.t("notion_database_card.open_link")}
                            <TbExternalLink class="size-3.5 shrink-0" aria-hidden="true" />
                          </a>
                        )}
                      </Show>
                    </li>
                  )}
                </For>
              </ul>

              <Show when={data().truncated}>
                <div class="border-t border-primary-border px-3 py-2 text-xs text-muted-text">
                  {rt.t("notion_database_card.truncated")}
                </div>
              </Show>

              <Show when={data().dataSources?.length ? data().dataSources : undefined}>
                {(sources) => (
                  <div class="truncate border-t border-primary-border px-3 py-2 text-xs text-muted-text">
                    {rt.t("notion_database_card.other_data_sources", {
                      names: sources()
                        .map((s) => s.name)
                        .join(", "),
                    })}
                  </div>
                )}
              </Show>
            </div>
          </Show>
        )}
      </Show>
    </Show>
  );
};
